import { Injectable } from '@angular/core';
import { gsap } from 'gsap';
import { registerRsPlugins, RsMotionService } from '../shared/motion';
import { rsFromTo } from '../shared/motion/rs-motion';

/**
 * Motion choreography for the Our Products marketplace page.
 *
 * Everything lives inside a single `gsap.context()` scoped to the page root, so
 * `ngOnDestroy` can tear the whole thing down with one `revert()` call. The
 * service owns no DOM state of its own — it only reads the scope it is handed.
 *
 * Guarantees:
 * • SSR-safe — nothing runs unless `RsMotionService.enabled` is true.
 * • `prefers-reduced-motion` is honoured; reduced visitors get the final state
 *   immediately instead of a tween.
 * • No element is left hidden if JS never runs: the pre-hide rules in CSS are
 *   gated behind `.rs-motion`, which only this service's `arm()` adds.
 * • `will-change` is set as the tween starts and cleared when it finishes.
 */
@Injectable()
export class OurProductsMotionService {
  private ctx?: gsap.Context;
  private cleanups: Array<() => void> = [];

  constructor(private motion: RsMotionService) {}

  /**
   * Builds every animation on the page.
   * Safe to call once from `ngAfterViewInit`; a no-op when motion is disabled.
   */
  init(root: HTMLElement): void {
    if (!this.motion.enabled) {
      // Reduced motion / server: reveal everything and stop.
      root
        .querySelectorAll<HTMLElement>('[data-op-reveal]')
        .forEach((el) => el.classList.add('rs-revealed'));
      return;
    }

    this.motion.arm();
    registerRsPlugins();

    this.ctx = gsap.context(() => {
      this.buildHero(root);
      this.buildCategories();
      this.buildFilters();
      this.buildCatalogue();
      this.buildStats();
      this.buildCta();
      this.buildDecorParallax();
    }, root);

    // pointer parallax for the hero showcase + ambient pause when off-screen
    const hero = root.querySelector<HTMLElement>('.op-hero');
    if (hero) {
      this.cleanups.push(this.motion.trackPointer(hero), this.motion.pauseOffscreen(hero));
    }

    this.bindMagnets(root);
  }

  /**
   * Magnetic hover: the button leans a few pixels toward the cursor and springs
   * back on leave. Bound only for devices that actually hover, so touch targets
   * are never offset from where the finger lands.
   */
  private bindMagnets(root: HTMLElement): void {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    root.querySelectorAll<HTMLElement>('.op-magnet').forEach((el) => {
      const to = {
        x: gsap.quickTo(el, 'x', { duration: 0.45, ease: 'power3.out' }),
        y: gsap.quickTo(el, 'y', { duration: 0.45, ease: 'power3.out' }),
      };

      const onMove = (e: PointerEvent) => {
        const r = el.getBoundingClientRect();
        to.x((e.clientX - (r.left + r.width / 2)) * 0.18);
        to.y((e.clientY - (r.top + r.height / 2)) * 0.3);
      };
      const onLeave = () => {
        to.x(0);
        to.y(0);
      };

      el.addEventListener('pointermove', onMove, { passive: true });
      el.addEventListener('pointerleave', onLeave);
      this.cleanups.push(() => {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
      });
    });
  }

  /* ------------------------------------------------------------------ hero -- */

  private buildHero(root: HTMLElement): void {
    const heading = root.querySelector<HTMLElement>('.op-hero__title');
    const words = heading ? this.splitWords(heading) : [];

    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    tl.fromTo('.op-hero__badge', ...rsFromTo({ opacity: 0, y: 18, duration: 0.6 }));

    if (words.length) {
      // word-level reveal — a local split, so no SplitType dependency is added
      tl.fromTo(
        words, ...rsFromTo({
          opacity: 0,
          yPercent: 110,
          duration: 0.8,
          stagger: 0.045,
          onStart: () => words.forEach((w) => (w.style.willChange = 'transform, opacity')),
          onComplete: () => words.forEach((w) => (w.style.willChange = 'auto')),
        }),
        '-=0.3'
      );
    }

    tl.fromTo('.op-hero__rule', ...rsFromTo({ opacity: 0, scaleX: 0, duration: 0.6 }), '-=0.5')
      .fromTo('.op-hero__lede', ...rsFromTo({ opacity: 0, y: 18, duration: 0.7 }), '-=0.45')
      .fromTo('.op-hero__ctas > *', ...rsFromTo({ opacity: 0, y: 18, duration: 0.6, stagger: 0.08 }), '-=0.45')
      .fromTo('.op-hero__trust li', ...rsFromTo({ opacity: 0, y: 14, duration: 0.55, stagger: 0.07 }), '-=0.4');

    // showcase panels float in behind the copy, then settle into a slow drift
    tl.fromTo(
      '.op-stage__layer', ...rsFromTo({
        opacity: 0,
        y: 46,
        scale: 0.94,
        duration: 0.9,
        stagger: 0.09,
        clearProps: 'opacity',
      }),
      '-=1.1'
    ).fromTo(
      '.op-chip', ...rsFromTo({ opacity: 0, scale: 0.7, duration: 0.6, stagger: 0.06, ease: 'back.out(1.6)' }),
      '-=0.5'
    );

    // Ambient chip float runs on the SAME transform channel GSAP just used.
    // Doing it here rather than in CSS avoids a CSS-animation-vs-inline-transform
    // fight that would swallow the entrance tween above.
    tl.add(() => this.floatChips());
  }

  /** Slow, endless bob on the hero micro-cards. */
  private floatChips(): void {
    gsap.utils.toArray<HTMLElement>('.op-chip').forEach((chip, i) => {
      gsap.to(chip, {
        y: -8,
        duration: 3.2 + (i % 3) * 0.4,
        delay: i * 0.18,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
    });
  }

  /* ------------------------------------------------------------ categories -- */

  private buildCategories(): void {
    const cards = gsap.utils.toArray<HTMLElement>('.op-cat');
    if (!cards.length) return;

    gsap.fromTo(cards, ...rsFromTo({
      opacity: 0,
      y: 26,
      duration: 0.65,
      stagger: 0.055,
      ease: 'power3.out',
      clearProps: 'transform,opacity',
      scrollTrigger: { trigger: '.op-cats', start: 'top 88%', once: true },
    }));
  }

  private buildFilters(): void {
    const pills = gsap.utils.toArray<HTMLElement>('.op-pill');
    if (!pills.length) return;

    gsap.fromTo(pills, ...rsFromTo({
      opacity: 0,
      y: 16,
      duration: 0.55,
      stagger: 0.045,
      ease: 'power3.out',
      clearProps: 'transform,opacity',
      scrollTrigger: { trigger: '.op-filters', start: 'top 92%', once: true },
    }));
  }

  /* ------------------------------------------------------------- catalogue -- */

  private buildCatalogue(): void {
    gsap.fromTo('.op-cat-head > *', ...rsFromTo({
      opacity: 0,
      y: 22,
      duration: 0.7,
      stagger: 0.08,
      ease: 'power3.out',
      scrollTrigger: { trigger: '.op-cat-head', start: 'top 88%', once: true },
    }));

    this.revealCards();
  }

  /**
   * Staggers the product cards in. Called again by the component after a filter
   * change so the newly rendered set animates instead of appearing abruptly.
   */
  revealCards(): void {
    if (!this.motion.enabled) return;

    const cards = gsap.utils.toArray<HTMLElement>('.op-card');
    if (!cards.length) return;

    gsap.killTweensOf(cards);
    gsap.fromTo(
      cards,
      { opacity: 0, y: 30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.62,
        stagger: 0.05,
        ease: 'power3.out',
        overwrite: 'auto',
        clearProps: 'transform',
        onStart: () => cards.forEach((c) => (c.style.willChange = 'transform, opacity')),
        onComplete: () => cards.forEach((c) => (c.style.willChange = 'auto')),
      }
    );
  }

  /* ----------------------------------------------------------------- stats -- */

  private buildStats(): void {
    gsap.fromTo('.op-stat', ...rsFromTo({
      opacity: 0,
      y: 26,
      duration: 0.65,
      stagger: 0.08,
      ease: 'power3.out',
      clearProps: 'transform,opacity',
      scrollTrigger: { trigger: '.op-stats', start: 'top 88%', once: true },
    }));

    // counters — GSAP-driven, so no extra CountUp dependency is pulled in
    gsap.utils.toArray<HTMLElement>('.op-stat__num[data-to]').forEach((el) => {
      const to = Number(el.dataset['to']);
      if (!Number.isFinite(to)) return;
      this.motion.countUp(el, to, 1.6);
    });
  }

  /* ------------------------------------------------------------------- cta -- */

  private buildCta(): void {
    gsap.fromTo('.op-cta__panel', ...rsFromTo({
      opacity: 0,
      y: 34,
      scale: 0.97,
      duration: 0.85,
      ease: 'power3.out',
      scrollTrigger: { trigger: '.op-cta', start: 'top 86%', once: true },
    }));

    // only the copy staggers — the mesh/blob layers are siblings of `__inner`
    gsap.fromTo('.op-cta__inner > *', ...rsFromTo({
      opacity: 0,
      y: 20,
      duration: 0.6,
      stagger: 0.07,
      ease: 'power3.out',
      scrollTrigger: { trigger: '.op-cta', start: 'top 82%', once: true },
    }));
  }

  /* --------------------------------------------------------- decor parallax -- */

  /** Slow vertical drift on the decorative blobs as the page scrolls. */
  private buildDecorParallax(): void {
    gsap.utils.toArray<HTMLElement>('.op-parallax').forEach((layer, i) => {
      gsap.to(layer, {
        yPercent: i % 2 === 0 ? -12 : 10,
        ease: 'none',
        scrollTrigger: {
          trigger: layer.closest('section') ?? layer,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        },
      });
    });
  }

  /* --------------------------------------------------------------- helpers -- */

  /**
   * Wraps each word in a `span.op-word > span.op-word__in` pair so the inner span
   * can rise out from behind the mask — a local split, so no SplitType dependency.
   *
   * `.op-grad` is wrapped whole rather than split: its gradient is painted on the
   * element's own background box and clipped to text, so slicing it into separate
   * inline-blocks would restart the ramp per word (and is flaky in some engines).
   * It animates as one unit, which is how Linear/Stripe treat gradient phrases.
   */
  private splitWords(el: HTMLElement): HTMLElement[] {
    if (el.dataset['opSplit'] === 'done') {
      return Array.from(el.querySelectorAll<HTMLElement>('.op-word__in'));
    }

    const mask = (child: Node): HTMLElement => {
      const outer = document.createElement('span');
      outer.className = 'op-word';
      const inner = document.createElement('span');
      inner.className = 'op-word__in';
      inner.appendChild(child);
      outer.appendChild(inner);
      return outer;
    };

    Array.from(el.childNodes).forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent ?? '';
        if (!text.trim()) return;

        const frag = document.createDocumentFragment();
        text.split(/(\s+)/).forEach((chunk) => {
          if (!chunk) return;
          frag.appendChild(
            chunk.trim()
              ? mask(document.createTextNode(chunk))
              : document.createTextNode(chunk)
          );
        });
        el.replaceChild(frag, child);
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        // keep gradient / styled spans intact — mask them as a single unit
        const wrapper = mask(child.cloneNode(true));
        el.replaceChild(wrapper, child);
      }
    });

    el.dataset['opSplit'] = 'done';
    return Array.from(el.querySelectorAll<HTMLElement>('.op-word__in'));
  }

  /** Tears down every tween, ScrollTrigger and listener this service created. */
  destroy(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
    this.ctx?.revert();
    this.ctx = undefined;
  }
}
